import type { ButtonProps as AntButtonProps } from 'antd';
import { Button as AntButton } from 'antd';
import React from 'react';
import styled from 'styled-components';
import { IconSize, IconType, IconWrap, Typography } from '../../..';
import { theme } from '../../theme/semanticTokens';

export type ButtonVariant =
  | 'secondary'
  | 'primary'
  | 'naked'
  | 'success'
  | 'critical'
  | 'successSecondary'
  | 'criticalSecondary';

export interface ButtonProps extends Omit<AntButtonProps, 'icon'> {
  variant?: ButtonVariant;
  iconLeft?: IconType;
  iconRight?: IconType;
  children?: React.ReactNode;
  size?: 'middle' | 'large';
  iconOnly?: IconType;
}

const commonStyles = `
  display: flex;
  align-items: center;
  gap: ${theme.dime.spacing.m};
  border-radius: ${theme.dime.radius.m};
  border: 1px solid ${theme.dime.colors.border.static};
  padding: ${theme.dime.spacing.l};

  .typography {
    color: inherit;
  }

  .icon-wrap {
    color: inherit;
  }

`;

const iconOnlyMiddleStyles = `
  padding: ${theme.dime.spacing.s} ${theme.dime.spacing.s};

  &.ant-btn {
    padding: 0 7px;
  }
`;

const iconOnlyLargeStyles = `
  padding: 4px 4px;
  &.ant-btn-lg {
    padding: 0 11px;
  }
`;

const disabledStyles = `
  color: ${theme.dime.colors.text.disabled};
  border-color: ${theme.dime.colors.border.interactiveDisabled};
  background-color: ${theme.dime.colors.action.secondaryDisabled};

  .icon-wrap {
    color: ${theme.dime.colors.text.disabled};
  }

  .typography {
    color: ${theme.dime.colors.text.disabled};
  }
`;

const StyledButton = styled(AntButton)<{
  $iconOnly?: IconType;
  $size?: 'middle' | 'large';
}>`
  ${commonStyles}

  ${({ $iconOnly, $size }) => {
    if (!$iconOnly) return '';
    return $size === 'middle' ? iconOnlyMiddleStyles : iconOnlyLargeStyles;
  }}

  /* Adjust padding when icons are present */
  &:has(.icon-left) {
    padding-left: ${theme.dime.spacing['2m']};
  }

  &:has(.icon-right) {
    padding-right: ${theme.dime.spacing['2m']};
  }

  &.primary {
    background: ${theme.dime.colors.action.primary};
    color: ${theme.dime.colors.text.inverse};
    border-color: ${theme.dime.colors.action.primary};

    &.ant-btn:hover:not([disabled]) {
      border-color: ${theme.dime.colors.action.primaryHover};
      color: ${theme.dime.colors.text.inverse};
      background-color: ${theme.dime.colors.action.primaryHover};
    }

    &.ant-btn-clicked:not([disabled]) {
      border-color: ${theme.dime.colors.action.primaryPressed};
      color: ${theme.dime.colors.text.inverse};
      background-color: ${theme.dime.colors.action.primaryPressed};
    }

    &.ant-dropdown-open {
      border-color: ${theme.dime.colors.action.primaryPressed};
      color: ${theme.dime.colors.text.inverse};
    }
  }

  &.secondary {
    background: ${theme.dime.colors.action.secondary};
    color: ${theme.dime.colors.text.primary};

    &.ant-btn:hover:not([disabled]) {
      border-color: ${theme.dime.colors.border.interactiveHover};
      color: ${theme.dime.colors.text.primary};
      background-color: ${theme.dime.colors.action.secondaryHover};
    }

    &.ant-btn-clicked:not([disabled]) {
      border-color: ${theme.dime.colors.border.interactiveSelected};
      color: ${theme.dime.colors.text.primary};
      background-color: ${theme.dime.colors.action.secondaryPressed};
    }

    &.ant-dropdown-open {
      border-color: ${theme.dime.colors.border.interactiveHover};
      color: ${theme.dime.colors.text.primary};
    }
  }

  &.naked {
    background: transparent;
    border: none;
    box-shadow: none;

    .typography {
      color: ${theme.dime.colors.link.primary};
    }

    .icon-wrap {
      color: ${theme.dime.colors.icon.link};
    }

    &.ant-btn:hover:not([disabled]) {
      background: transparent;
      border: none;
      .typography {
        color: ${theme.dime.colors.link.primaryHover};
      }

      .icon-wrap {
        color: ${theme.dime.colors.link.primaryHover};
      }
    }

    &.ant-btn-clicked:not([disabled]) {
      background: ${theme.dime.colors.action.tertiaryPressed};
      color: ${theme.dime.colors.link.primaryPressed};
      border: none;
      .typography {
        color: ${theme.dime.colors.link.primaryPressed};
      }
      .icon-wrap {
        color: ${theme.dime.colors.link.primaryPressed};
      }
    }

    &.ant-dropdown-open {
      background: ${theme.dime.colors.action.tertiaryPressed};
      color: ${theme.dime.colors.link.primaryPressed};
      border: none;
    }
  }

  &.success {
    background: ${theme.dime.colors.action.positive};
    color: ${theme.dime.colors.text.inverse};
    border-color: ${theme.dime.colors.action.positive};

    &.ant-btn:hover:not([disabled]) {
      border-color: ${theme.dime.colors.action.successHover};
      color: ${theme.dime.colors.text.inverse};
      background-color: ${theme.dime.colors.action.successHover};
    }

    &.ant-btn-clicked:not([disabled]) {
      border-color: ${theme.dime.colors.action.successPressed};
      color: ${theme.dime.colors.text.inverse};
      background-color: ${theme.dime.colors.action.successPressed};
    }

    &.ant-dropdown-open {
      border-color: ${theme.dime.colors.action.successPressed};
      color: ${theme.dime.colors.text.inverse};
    }
  }

  &.critical {
    background: ${theme.dime.colors.action.criticalPrimary};
    color: ${theme.dime.colors.text.inverse};
    border-color: ${theme.dime.colors.action.criticalPrimary};

    &.ant-btn:hover:not([disabled]) {
      border-color: ${theme.dime.colors.action.criticalPrimaryHover};
      color: ${theme.dime.colors.text.inverse};
      background-color: ${theme.dime.colors.action.criticalPrimaryHover};
    }

    &.ant-btn-clicked:not([disabled]) {
      border-color: ${theme.dime.colors.action.criticalPrimaryPressed};
      color: ${theme.dime.colors.text.inverse};
      background-color: ${theme.dime.colors.action.criticalPrimaryPressed};
    }

    &.ant-dropdown-open {
      background: ${theme.dime.colors.action.criticalPrimaryPressed};
      border-color: ${theme.dime.colors.action.criticalPrimaryPressed};
      color: ${theme.dime.colors.text.inverse};
    }
  }

  &.successSecondary {
    background: transparent;
    color: ${theme.dime.colors.text.success};
    border-color: ${theme.dime.colors.border.static};

    .typography {
      color: ${theme.dime.colors.text.success};
    }

    .icon-wrap {
      color: ${theme.dime.colors.text.success};
    }

    &.ant-btn:hover:not([disabled]) {
      background: transparent;
      color: ${theme.dime.colors.text.successHover};
      border-color: ${theme.dime.colors.border.success};

      .icon-wrap {
        color: ${theme.dime.colors.text.successHover};
      }
    }

    &.ant-btn-clicked:not([disabled]) {
      background-color: ${theme.dime.colors.action.positiveSecondaryPressed};
      color: ${theme.dime.colors.text.successOnColor};
      border-color: ${theme.dime.colors.border.success};

      .icon-wrap {
        color: ${theme.dime.colors.text.successOnColor};
      }
    }

    &.ant-dropdown-open {
      background-color: ${theme.dime.colors.action.positiveSecondaryPressed};
      color: ${theme.dime.colors.text.successOnColor};
      border-color: ${theme.dime.colors.border.success};
    }
  }

  &.criticalSecondary {
    background: transparent;
    color: ${theme.dime.colors.text.critical};
    border-color: ${theme.dime.colors.border.static};

    .typography {
      color: ${theme.dime.colors.text.critical};
    }

    .icon-wrap {
      color: ${theme.dime.colors.text.critical};
    }

    &.ant-btn:hover:not([disabled]) {
      background: ${theme.dime.colors.action.criticalSecondaryHover};
      color: ${theme.dime.colors.text.criticalHover};
      border-color: ${theme.dime.colors.border.critical};

      .icon-wrap {
        color: ${theme.dime.colors.text.criticalHover};
      }
    }

    &.ant-btn-clicked:not([disabled]) {
      background: ${theme.dime.colors.action.criticalSecondaryPressed};
      color: ${theme.dime.colors.text.criticalOnColor};
      border-color: ${theme.dime.colors.border.critical};

      .icon-wrap {
        color: ${theme.dime.colors.text.criticalOnColor};
      }
    }

    &.ant-dropdown-open {
      background: ${theme.dime.colors.action.criticalSecondaryPressed};
      color: ${theme.dime.colors.text.criticalOnColor};
      border-color: ${theme.dime.colors.border.critical};
    }
  }

  &.ant-btn:disabled {
    ${disabledStyles}
  }

  &.primary.ant-btn:disabled {
    background: ${theme.dime.colors.action.primaryDisabled};
  }
  &.secondary.ant-btn:disabled {
    background: ${theme.dime.colors.action.secondaryDisabled};
  }

  &.naked.ant-btn:disabled {
    background: ${theme.dime.colors.action.tertiaryDisabled};
    border: none;
  }

  &.success.ant-btn:disabled {
    background: ${theme.dime.colors.action.successDisabled};
  }

  &.critical.ant-btn:disabled {
    background: ${theme.dime.colors.action.criticalPrimaryDisabled};
  }

  &.successSecondary.ant-btn:disabled {
    background: ${theme.dime.colors.action.positiveSecondaryDisabled};
  }

  &.criticalSecondary.ant-btn:disabled {
    background: ${theme.dime.colors.action.criticalSecondaryDisabled};
  }
`;

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'large',
  iconLeft,
  iconRight,
  children,
  iconOnly,
  className = '',
  ...props
}) => {
  const isIconOnly = !!iconOnly;

  return (
    <StyledButton
      className={`${variant} ${className}`}
      $iconOnly={iconOnly}
      $size={size}
      size={size}
      {...props}
    >
      {!!isIconOnly && (
        <IconWrap type={iconOnly} size={IconSize.Small} className="icon-wrap" />
      )}
      {!isIconOnly && !!iconLeft && (
        <IconWrap
          size={IconSize.Small}
          type={iconLeft}
          className="icon-wrap icon-left"
        />
      )}
      {!isIconOnly && !!children && (
        <Typography variant="body1" className="typography">
          {children}
        </Typography>
      )}
      {!isIconOnly && !!iconRight && (
        <IconWrap
          size={IconSize.Small}
          type={iconRight}
          className="icon-wrap icon-right"
        />
      )}
    </StyledButton>
  );
};
